import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../utils/dateUtils';
import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { Table } from '../components/ui/Table';
import {
  useGetNotificationsQuery,
  useScheduleNotificationMutation,
  useUpdateNotificationMutation,
  useDeleteNotificationMutation,
} from '../store/slices/apiSlice';
import type { Notification as NotificationType } from '../store/slices/apiSlice';
import {
  Plus,
  Edit,
  Trash2,
  Bell,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Info,
  Search,
  User,
  Users,
  Clock
} from 'lucide-react';
import toast from 'react-hot-toast';

const Notification: React.FC = () => {
  const { data: notificationsResponse, isLoading, refetch } = useGetNotificationsQuery();
  const [scheduleNotification, { isLoading: isScheduling }] = useScheduleNotificationMutation();
  const [updateNotification, { isLoading: isUpdating }] = useUpdateNotificationMutation();
  const [deleteNotification, { isLoading: isDeleting }] = useDeleteNotificationMutation();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<NotificationType | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    notification_for: 'user', // Always set to 'user' by default
    schedule_at: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const notifications = notificationsResponse?.data || [];

  // Filter notifications based on search term
  const filteredNotifications = notifications.filter(notification =>
    notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    notification.message.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Clear form errors when modal is closed
  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setFormData({
      title: '',
      message: '',
      notification_for: 'user', // Always set to 'user'
      schedule_at: '',
    });
    setFormErrors({});
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedNotification(null);
    setFormData({
      title: '',
      message: '',
      notification_for: 'user', // Always set to 'user'
      schedule_at: '',
    });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    }
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    }
    if (!formData.schedule_at) {
      errors.schedule_at = 'Schedule date is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateNotification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const result = await scheduleNotification({
        title: formData.title.trim(),
        message: formData.message.trim(),
        schedule_at: new Date(formData.schedule_at).toISOString(),
        notification_for: formData.notification_for,
      }).unwrap();

      if (result.success) {
        toast.success('Notification created successfully!');
        handleCloseCreateModal();
        refetch();
      } else {
        toast.error(result.message || 'Failed to create notification');
      }
    } catch (error: any) {
      toast.error(error?.data?.message || 'An error occurred while creating notification');
    }
  };

  const handleUpdateNotification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedNotification || !validateForm()) return;

    try {
      const result = await updateNotification({
        notification_id: selectedNotification.notification_id,
        title: formData.title.trim(),
        message: formData.message.trim(),
        schedule_at: new Date(formData.schedule_at).toISOString(),
        notification_for: formData.notification_for,
      }).unwrap();

      if (result.success) {
        toast.success('Notification updated successfully!');
        handleCloseEditModal();
        refetch();
      } else {
        toast.error(result.message || 'Failed to update notification');
      }
    } catch (error: any) {
      toast.error(error?.data?.message || 'An error occurred while updating notification');
    }
  };

  const handleDeleteNotification = async () => {
    if (!selectedNotification) return;

    try {
      const result = await deleteNotification(selectedNotification.notification_id).unwrap();
      if (result.success) {
        toast.success('Notification deleted successfully!');
        setIsDeleteModalOpen(false);
        setSelectedNotification(null);
        refetch();
      } else {
        toast.error(result.message || 'Failed to delete notification');
      }
    } catch (error: any) {
      toast.error(error?.data?.message || 'An error occurred while deleting notification');
    }
  };

  const openEditModal = (notification: NotificationType) => {
    setSelectedNotification(notification);

    // Convert UTC date to local datetime-local format
    let localDateTime = '';
    if (notification.schedule_at) {
      const date = new Date(notification.schedule_at);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      localDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    setFormData({
      title: notification.title,
      message: notification.message,
      notification_for: notification.notification_for,
      schedule_at: localDateTime,
    });
    setFormErrors({});
    setIsEditModalOpen(true);
  };

  const openDeleteModal = (notification: NotificationType) => {
    setSelectedNotification(notification);
    setIsDeleteModalOpen(true);
  };

  const getNotificationTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Info className="w-4 h-4 text-blue-600" />;
    }
  };

  const getNotificationTypeBadge = (type: string) => {
    switch (type.toLowerCase()) {
      case 'success':
        return <Badge variant="success" className="text-xs">Success</Badge>;
      case 'warning':
        return <Badge variant="warning" className="text-xs">Warning</Badge>;
      case 'error':
        return <Badge variant="danger" className="text-xs">Error</Badge>;
      default:
        return <Badge variant="info" className="text-xs">Info</Badge>;
    }
  };

  const formatDate = (dateString: string | Date | null | undefined) => formatDateTimeUtil(dateString);;

  // Check if notification is past scheduled time
  const isNotificationSent = (scheduleAt: string) => {
    if (!scheduleAt) return false;
    const scheduledTime = new Date(scheduleAt);
    const currentTime = new Date();
    return scheduledTime < currentTime;
  };

  // Get notification status
  const getNotificationStatus = (scheduleAt: string) => {
    if (!scheduleAt) return 'pending';
    const scheduledTime = new Date(scheduleAt);
    const currentTime = new Date();

    if (scheduledTime < currentTime) {
      return 'success'; // Sent
    } else {
      return 'pending'; // Pending to send
    }
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <Badge variant="success" className="text-xs">Sent</Badge>;
      case 'pending':
        return <Badge variant="warning" className="text-xs">Pending</Badge>;
      default:
        return <Badge variant="secondary" className="text-xs">Unknown</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Notification Management</h1>
          <p className="text-sm text-gray-600">Manage system notifications and alerts</p>
        </div>
        <Button
          onClick={() => setIsCreateModalOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-sm px-4 py-2"
        >
          <Plus className="w-4 h-4 mr-2" /> Create Notification
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Bell className="w-5 h-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-600">Total Notifications</p>
              <p className="text-lg font-semibold text-gray-900">{notifications.length}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-600">Sent</p>
              <p className="text-lg font-semibold text-gray-900">
                {notifications.filter(n => isNotificationSent(n.schedule_at)).length}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-600">Pending</p>
              <p className="text-lg font-semibold text-gray-900">
                {notifications.filter(n => !isNotificationSent(n.schedule_at)).length}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-600">Errors</p>
              <p className="text-lg font-semibold text-gray-900">
                {notifications.filter(n => n.notification_type.toLowerCase() === 'error').length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search */}
      <Card className="p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search notifications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </Card>

      {/* Notifications List */}
      <Card className="p-4">
        {filteredNotifications.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 mb-4">
              <Bell className="w-12 h-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'No notifications found' : 'No notifications yet'}
            </h3>
            <p className="text-gray-600 mb-4">
              {searchTerm
                ? 'Try adjusting your search terms.'
                : 'Create your first notification to get started.'
              }
            </p>
            {!searchTerm && (
              <Button
                onClick={() => setIsCreateModalOpen(true)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" /> Create Notification
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table
              columns={[
                {
                  key: 'sno',
                  label: 'S.No',
                  render: (_: any, __: any, index: number) => (
                    <div className="font-bold text-gray-400 text-xs">#{index}</div>
                  ),
                },
                {
                  key: 'title',
                  label: 'Title',
                  render: (value) => (
                    <div className="font-medium text-gray-900 text-sm">{value}</div>
                  ),
                },
                {
                  key: 'message',
                  label: 'Message',
                  render: (value) => (
                    <div className="text-gray-600 text-sm max-w-xs truncate">{value}</div>
                  ),
                },

                {
                  key: 'notification_for',
                  label: 'For',
                  render: (value) => (
                    <div className="flex items-center space-x-1">
                      {value === 'user' ? <User className="w-3 h-3 text-gray-400" /> : <Users className="w-3 h-3 text-gray-400" />}
                      <span className="text-xs text-gray-600 capitalize">{value}</span>
                    </div>
                  ),
                },
                {
                  key: 'schedule_at',
                  label: 'Scheduled',
                  render: (value) => (
                    <div className="flex items-center space-x-1">
                      <Clock className="w-3 h-3 text-gray-400" />
                      <span className="text-xs text-gray-500">{formatDate(value)}</span>
                    </div>
                  ),
                },
                {
                  key: 'status',
                  label: 'Status',
                  render: (_, row) => getStatusBadge(getNotificationStatus(row.schedule_at)),
                },
                {
                  key: 'actions',
                  label: 'Actions',
                  render: (_, row) => (
                    <div className="flex space-x-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditModal(row)}
                        className="text-xs px-2 py-1"
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => openDeleteModal(row)}
                        className="text-xs px-2 py-1"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ),
                },
              ]}
              data={filteredNotifications}
            />
          </div>
        )}
      </Card>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        title="Create Notification"
        size="lg"
      >
        <form onSubmit={handleCreateNotification} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Title *
              </label>
              <Input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter notification title"
                className={formErrors.title ? 'border-red-500' : ''}
              />
              {formErrors.title && (
                <p className="text-xs text-red-500 mt-1">{formErrors.title}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Message *
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Enter notification message"
                rows={3}
                className={`w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${formErrors.message ? 'border-red-500' : ''}`}
              />
              {formErrors.message && (
                <p className="text-xs text-red-500 mt-1">{formErrors.message}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Schedule Date & Time *
              </label>
              <input
                type="datetime-local"
                value={formData.schedule_at}
                onChange={(e) => setFormData({ ...formData, schedule_at: e.target.value })}
                className={`w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${formErrors.schedule_at ? 'border-red-500' : ''}`}
              />
              {formErrors.schedule_at && (
                <p className="text-xs text-red-500 mt-1">{formErrors.schedule_at}</p>
              )}
            </div>
          </div>

          {/* Sticky bottom buttons */}
          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={handleCloseCreateModal}
              className="w-full sm:w-auto text-sm px-4 py-2"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isScheduling}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-sm px-4 py-2"
            >
              {isScheduling ? 'Creating...' : 'Create Notification'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        title="Edit Notification"
        size="lg"
      >
        <form onSubmit={handleUpdateNotification} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Title *
              </label>
              <Input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter notification title"
                className={formErrors.title ? 'border-red-500' : ''}
              />
              {formErrors.title && (
                <p className="text-xs text-red-500 mt-1">{formErrors.title}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Message *
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Enter notification message"
                rows={3}
                className={`w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${formErrors.message ? 'border-red-500' : ''}`}
              />
              {formErrors.message && (
                <p className="text-xs text-red-500 mt-1">{formErrors.message}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Schedule Date & Time *
              </label>
              <input
                type="datetime-local"
                value={formData.schedule_at}
                onChange={(e) => setFormData({ ...formData, schedule_at: e.target.value })}
                className={`w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${formErrors.schedule_at ? 'border-red-500' : ''}`}
              />
              {formErrors.schedule_at && (
                <p className="text-xs text-red-500 mt-1">{formErrors.schedule_at}</p>
              )}
            </div>
          </div>

          {/* Sticky bottom buttons */}
          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={handleCloseEditModal}
              className="w-full sm:w-auto text-sm px-4 py-2"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isUpdating}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-sm px-4 py-2"
            >
              {isUpdating ? 'Updating...' : 'Update Notification'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Notification"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Are you sure you want to delete the notification{' '}
            <span className="font-semibold">{selectedNotification?.title}</span>?
            This action cannot be undone.
          </p>

          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
              className="w-full sm:w-auto text-sm px-4 py-2"
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteNotification}
              disabled={isDeleting}
              className="w-full sm:w-auto text-sm px-4 py-2"
            >
              {isDeleting ? 'Deleting...' : 'Delete Notification'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Notification;
